import { Box, Drawer } from '@mui/material';
import { ChangeEvent, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { TextField } from 'src/components/new/inputs/TextField';
import { ModalContent } from 'src/components/new/modal/ModalContent';
import { ModalFooter } from 'src/components/new/modal/ModalFooter';
import { ModalTitle } from 'src/components/new/modal/ModalTitle';
import { updatePromoCodeForm } from 'src/features/admin/adminSlice';
import { AppDispatch, RootState } from 'src/store/store';
import { renderStatus } from '../pricing/PromoCodes';
import { Button } from 'src/components/new/Button';
import { PromoCode } from 'src/features/admin/constants';
import { isValidDate } from 'src/utils/utility-functions';

interface AddPromoCodeProps {
  open: boolean;
  mode: 'add' | 'edit';
  onClose: () => void;
  onStatusChange: (data: { id: number; endAt: Date; printCreditEndAt: Date; isActivated: boolean }) => void;
  onSuccess: () => void;
}

const AddPromoCode: React.FC<AddPromoCodeProps> = ({ open, mode, onClose, onSuccess, onStatusChange }) => {
  const [formErrors, setFormErrors] = useState({
    name: '',
    startAt: '',
    endAt: '',
    printCreditEndAt: '',
    printCreditAmount: '',
  });

  const dispatch: AppDispatch = useDispatch();
  const promoCodeForm = useSelector((state: RootState) => state.admin.promoCodeForm);
  const promoCodes = useSelector((state: RootState) => state.admin.promoCodes);

  const handlePromoCodeFormChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.name === 'startAt' || e.target.name === 'endAt' || e.target.name === 'printCreditEndAt') {
      let value = e.target.value.replace(/\D/g, '');

      if (value.length > 8) {
        value = value.slice(0, 8);
      }

      let formatted = '';
      if (value.length > 0) {
        formatted += value.substring(0, 2);
      }
      if (value.length > 2) {
        formatted += '/' + value.substring(2, 4);
      }
      if (value.length > 4) {
        formatted += '/' + value.substring(4, 8);
      }

      const updatedForm = { ...promoCodeForm, [e.target.name]: formatted };
      dispatch(updatePromoCodeForm(updatedForm));
    } else {
      const updatedForm = { ...promoCodeForm, [e.target.name]: e.target.value };
      dispatch(updatePromoCodeForm(updatedForm));
    }
  };

  const renderAction = () => {
    const selectedCode = promoCodes.find((code) => code.name === promoCodeForm.name) as PromoCode;

    if (new Date() > new Date(selectedCode?.endAt))
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mb: 2, mt: -2 }}>
          {renderStatus({ isActivated: selectedCode?.isActivated, endAt: selectedCode?.endAt })}
        </Box>
      );

    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mb: 2, mt: -2 }}>
        {renderStatus({ isActivated: selectedCode?.isActivated, endAt: selectedCode?.endAt })}
        <Button
          onClick={() =>
            onStatusChange({
              id: selectedCode.id,
              endAt: new Date(selectedCode.endAt),
              printCreditEndAt: new Date(selectedCode.printCreditEndAt),
              isActivated: !selectedCode.isActivated,
            })
          }
          sx={{ width: '12.5rem' }}
          type="outline"
        >
          {selectedCode?.isActivated ? 'Deactivate' : 'Reactivate'}
        </Button>
      </Box>
    );
  };

  const handleAddPromoCode = () => {
    const newErrors: any = {};

    if (promoCodeForm.name === '') {
      newErrors.name = 'Name cannot be empty';
    }

    if (promoCodeForm.endAt === '') {
      newErrors.endAt = 'Promo End cannot be empty';
    }

    if (promoCodeForm.startAt === '') {
      newErrors.startAt = 'Promo Start cannot be empty';
    }

    if (mode === 'add' && promoCodeForm.startAt !== '' && !isValidDate(promoCodeForm.startAt)) {
      newErrors.startAt = 'Provided date must be valid';
    }

    if (promoCodeForm.endAt !== '' && !isValidDate(promoCodeForm.endAt)) {
      newErrors.endAt = 'Provided date must be valid';
    }

    if (promoCodeForm.printCreditEndAt !== '' && !isValidDate(promoCodeForm.printCreditEndAt)) {
      newErrors.printCreditEndAt = 'Provided date must be valid';
    }

    if (
      promoCodeForm.startAt !== '' &&
      promoCodeForm.endAt !== '' &&
      new Date(promoCodeForm.startAt) > new Date(promoCodeForm.endAt)
    ) {
      newErrors.endAt = 'Promo end date should be after start date';
    }

    if (promoCodeForm.printCreditAmount === '') {
      newErrors.printCreditAmount = 'Print Credit amount cannot be empty';
    }

    if (promoCodeForm.printCreditEndAt === '') {
      newErrors.printCreditEndAt = 'Credit expiration cannot be emtpy';
    }

    if (promoCodeForm.printCreditAmount !== '' && Number(promoCodeForm.printCreditAmount) < 0) {
      newErrors.printCreditAmount = 'Print credit amount must be above 0';
    }

    if (mode === 'add' && promoCodeForm.name !== '' && promoCodes.some((p) => p.name === promoCodeForm.name)) {
      newErrors.name = 'Such promo code already exists';
    }

    setFormErrors(newErrors);

    const hasErrors = Object.values(newErrors).some((error) => error !== '');

    if (!hasErrors) {
      onSuccess();
    }
  };

  const handleClose = () => {
    onClose();
    setFormErrors({
      name: '',
      startAt: '',
      endAt: '',
      printCreditEndAt: '',
      printCreditAmount: '',
    });
  };

  return (
    <>
      <Drawer
        open={open}
        anchor="right"
        onClose={handleClose}
        sx={{
          '& .MuiPaper-root': {
            background: '#FFF',
            width: '31.25rem',
          },
        }}
      >
        <ModalTitle small onClose={handleClose}>
          {mode === 'add' ? 'Create' : 'Edit'} Promo Code
        </ModalTitle>
        <ModalContent>
          {mode === 'edit' && renderAction()}
          <TextField
            required
            value={promoCodeForm.name}
            name="name"
            id="name"
            inputProps={{ maxLength: 255 }}
            onChange={handlePromoCodeFormChange}
            error={formErrors.name}
            placeholder="Add..."
            disabled={mode === 'edit'}
            label="Promo Code"
          />
          <div style={{ display: 'flex', gap: '0.625rem', flexWrap: 'nowrap' }}>
            <TextField
              required
              value={promoCodeForm.startAt}
              name="startAt"
              id="startAt"
              disabled={mode === 'edit'}
              onChange={handlePromoCodeFormChange}
              error={formErrors.startAt}
              placeholder="MM/DD/YYYY"
              label="Promo Start"
            />
            <TextField
              required
              value={promoCodeForm.endAt}
              name="endAt"
              id="endAt"
              placeholder="MM/DD/YYYY"
              onChange={handlePromoCodeFormChange}
              error={formErrors.endAt}
              label="Promo End"
            />
          </div>
          <TextField
            required
            value={promoCodeForm.printCreditEndAt}
            name="printCreditEndAt"
            placeholder="MM/DD/YYYY"
            id="printCreditEndAt"
            onChange={handlePromoCodeFormChange}
            sx={{ width: '49%' }}
            error={formErrors.printCreditEndAt}
            label="Credit Expiration"
          />
          <TextField
            required
            value={promoCodeForm.printCreditAmount}
            name="printCreditAmount"
            id="printCreditAmount"
            placeholder="$"
            disabled={mode === 'edit'}
            onChange={handlePromoCodeFormChange}
            type="number"
            error={formErrors.printCreditAmount}
            label="Print Credit Amount"
          />
        </ModalContent>
        <ModalFooter onSubmit={() => handleAddPromoCode()} submitText="Save" />
      </Drawer>
    </>
  );
};

export default AddPromoCode;
